import { useState } from "react";
import { useApolloClient } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { toast } from "@outpave/ds";
import AccountType from "enums/AccountType";
import { COMPLETE_BANK_LINK } from "graphql/company";
import { getUserFacingErrorMessage, isUserFacingError } from "utils/graphql";

export interface CompleteBankLinkInput {
  programId?: string;
  type?: AccountType;
  accountId?: string;
  publicToken?: string;
  accountIds?: string[];
  institutionName?: string;
  authorizedAt?: number;
}

export interface CompletedBankLink {
  accounts: {
    id: string;
    bankName?: string | null;
    last4?: string | null;
    status?: string | null;
  }[];
  skipped: {
    name: string | null;
    last4: string | null;
    reason: string | null;
  }[];
}

// The server answers a repeated completion with the banks it saved, so a
// failure that may have lost the reply is sent again once, then offers Retry.
const useCompleteBankLink = () => {
  const client = useApolloClient();
  const { t } = useTranslation();
  const [completing, setCompleting] = useState(false);

  const send = async (
    input: CompleteBankLinkInput,
  ): Promise<CompletedBankLink> => {
    const { data } = await client.mutate<{
      completeBankLink: CompletedBankLink;
    }>({ mutation: COMPLETE_BANK_LINK, variables: { input } });
    return {
      accounts: data?.completeBankLink.accounts ?? [],
      skipped: data?.completeBankLink.skipped ?? [],
    };
  };

  const offerRetry = (
    input: CompleteBankLinkInput,
    onRetried: (link: CompletedBankLink) => void,
    error: unknown,
  ) => {
    toast.error(getUserFacingErrorMessage(error, t("BankLinkFailed")), {
      duration: false,
      action: {
        label: t("Retry"),
        onClick: () => {
          send(input).then(onRetried, (again: unknown) =>
            offerRetry(input, onRetried, again),
          );
        },
      },
    });
  };

  // The Retry toast outlives the flow, so its success goes to onRetried.
  const completeBankLink = async (
    input: CompleteBankLinkInput,
    onRetried: (link: CompletedBankLink) => void,
  ): Promise<CompletedBankLink | undefined> => {
    setCompleting(true);
    try {
      return await send(input);
    } catch (error) {
      if (isUserFacingError(error)) {
        toast.error(getUserFacingErrorMessage(error, t("BankLinkFailed")));
        return undefined;
      }
      try {
        return await send(input);
      } catch (again) {
        offerRetry(input, onRetried, again);
        return undefined;
      }
    } finally {
      setCompleting(false);
    }
  };

  return { completeBankLink, completing };
};

export default useCompleteBankLink;
